import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PaymentManagementPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Box
          sx={{
            background: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '0 0 32px 32px',
            p: { xs: 2, sm: 3, md: 5 },
            mb: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: { xs: 2, md: 4 }
          }}>
            <Box>
              <Skeleton height={isMobile ? 28 : 40} width={isMobile ? 180 : 250} style={{ marginBottom: 8 }} />
              <Skeleton height={isMobile ? 16 : 20} width={isMobile ? '100%' : 350} />
            </Box>
            <Skeleton height={isMobile ? 40 : 45} width={isMobile ? 100 : 120} style={{ borderRadius: '12px' }} />
          </Box>

          <Box sx={{
            background: '#f8fafc',
            borderRadius: '16px',
            padding: '8px',
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}>
            {[...Array(isMobile ? 3 : 5)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: index === 0 ? 'white' : 'transparent',
                  borderRadius: '12px',
                  px: { xs: 2, md: 3 },
                  py: 1.5,
                  minWidth: { xs: 80, md: 100 },
                  flexShrink: 0,
                }}
              >
                <Skeleton
                  height={16}
                  width={isMobile ? 60 : 80}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
          <Paper sx={{
            borderRadius: '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Skeleton height={16} width={100} />
                      <Skeleton height={20} width={60} style={{ borderRadius: 8 }} />
                    </Box>
                    <Skeleton height={14} width="70%" style={{ marginBottom: 4 }} />
                    <Skeleton height={14} width="50%" style={{ marginBottom: 8 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Skeleton height={14} width={80} />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Skeleton circle width={32} height={32} />
                        <Skeleton circle width={32} height={32} />
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell><Skeleton height={20} width={100} /></TableCell>
                      <TableCell><Skeleton height={20} width={80} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={20} width={100} /></TableCell>}
                      <TableCell><Skeleton height={20} width={100} /></TableCell>
                      <TableCell><Skeleton height={20} width={80} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={20} width={80} /></TableCell>}
                      <TableCell align="right"><Skeleton height={20} width={80} /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(8)].map((_, index) => (
                      <TableRow
                        key={index}
                        sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}
                      >
                        <TableCell><Skeleton height={20} width={120} /></TableCell>
                        <TableCell><Skeleton height={20} width={100} /></TableCell>
                        {!isTablet && <TableCell><Skeleton height={20} width={120} /></TableCell>}
                        <TableCell><Skeleton height={20} width={90} /></TableCell>
                        <TableCell><Skeleton height={24} width={80} style={{ borderRadius: '12px' }} /></TableCell>
                        {!isTablet && <TableCell><Skeleton height={16} width={120} /></TableCell>}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Skeleton circle width={36} height={36} />
                            <Skeleton circle width={36} height={36} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{
              p: 2,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' }
            }}>
              <Skeleton height={40} width={isMobile ? 200 : 300} />
            </Box>
          </Paper>
        </Box>
      </Box>
    </SkeletonTheme>
  );
};

export default PaymentManagementPageSkeleton;
